<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Relatorio Detalhado por Agente',
  },
});

const sortField = ref('conversations_count');
const sortAsc = ref(false);

const sortedData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return [...props.data].sort((a, b) => {
    let valA = a[sortField.value] || 0;
    let valB = b[sortField.value] || 0;
    if (sortAsc.value) return valA - valB;
    return valB - valA;
  });
});

function toggleSort(field) {
  if (sortField.value === field) {
    sortAsc.value = !sortAsc.value;
  } else {
    sortField.value = field;
    sortAsc.value = false;
  }
}

function getSortIcon(field) {
  if (sortField.value !== field) return 'i-lucide-arrow-up-down';
  return sortAsc.value ? 'i-lucide-arrow-up' : 'i-lucide-arrow-down';
}

function formatTime(minutes) {
  if (!minutes || minutes === 0) return '-';
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)}h`;
}

function formatRate(rate) {
  if (!rate && rate !== 0) return '-';
  return `${rate}%`;
}

function getStatusColor(status) {
  switch (status) {
    case 'online':
      return 'bg-n-teal-9';
    case 'busy':
      return 'bg-n-amber-9';
    case 'offline':
      return 'bg-n-slate-7';
    default:
      return 'bg-n-slate-5';
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'online':
      return 'Online';
    case 'busy':
      return 'Ocupado';
    case 'offline':
      return 'Offline';
    default:
      return 'Desc.';
  }
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-table text-lg text-n-blue-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      <span class="ml-auto text-xs text-n-slate-11">{{ sortedData.length }} agentes</span>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedData.length === 0"
      class="flex items-center justify-center h-64 text-n-slate-11"
    >
      <span class="text-sm">Sem dados de agentes</span>
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak">
            <th class="text-left py-2 px-2 text-xs font-medium text-n-slate-11">
              Agente
            </th>
            <th
              class="text-center py-2 px-2 text-xs font-medium text-n-slate-11"
            >
              Status
            </th>
            <th
              class="text-right py-2 px-2 text-xs font-medium text-n-slate-11 cursor-pointer hover:text-n-slate-12"
              @click="toggleSort('conversations_count')"
            >
              <span class="flex items-center justify-end gap-1">
                Conv.
                <span
                  :class="getSortIcon('conversations_count')"
                  class="text-xs"
                />
              </span>
            </th>
            <th
              class="text-right py-2 px-2 text-xs font-medium text-n-slate-11 cursor-pointer hover:text-n-slate-12"
              @click="toggleSort('resolved_count')"
            >
              <span class="flex items-center justify-end gap-1">
                Resol.
                <span :class="getSortIcon('resolved_count')" class="text-xs" />
              </span>
            </th>
            <th
              class="text-right py-2 px-2 text-xs font-medium text-n-slate-11 cursor-pointer hover:text-n-slate-12"
              @click="toggleSort('resolution_rate')"
            >
              <span class="flex items-center justify-end gap-1">
                Taxa
                <span :class="getSortIcon('resolution_rate')" class="text-xs" />
              </span>
            </th>
            <th
              class="text-right py-2 px-2 text-xs font-medium text-n-slate-11 cursor-pointer hover:text-n-slate-12"
              @click="toggleSort('avg_first_response_minutes')"
            >
              <span class="flex items-center justify-end gap-1">
                1a Resp.
                <span
                  :class="getSortIcon('avg_first_response_minutes')"
                  class="text-xs"
                />
              </span>
            </th>
            <th
              class="text-right py-2 px-2 text-xs font-medium text-n-slate-11 cursor-pointer hover:text-n-slate-12"
              @click="toggleSort('csat_score')"
            >
              <span class="flex items-center justify-end gap-1">
                CSAT
                <span :class="getSortIcon('csat_score')" class="text-xs" />
              </span>
            </th>
            <th
              class="text-right py-2 px-2 text-xs font-medium text-n-slate-11 cursor-pointer hover:text-n-slate-12"
              @click="toggleSort('sla_compliance')"
            >
              <span class="flex items-center justify-end gap-1">
                SLA
                <span :class="getSortIcon('sla_compliance')" class="text-xs" />
              </span>
            </th>
            <th
              class="text-right py-2 px-2 text-xs font-medium text-n-slate-11"
            >
              Transf.
            </th>
            <th
              class="text-right py-2 px-2 text-xs font-medium text-n-slate-11"
            >
              Msgs
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="agent in sortedData"
            :key="agent.agent_id"
            class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors"
          >
            <td class="py-2 px-2">
              <div class="flex items-center gap-2">
                <Avatar
                  :name="agent.name"
                  :src="agent.thumbnail"
                  :size="28"
                  rounded-full
                />
                <div class="min-w-0">
                  <div class="text-sm font-medium text-n-slate-12 truncate">
                    {{ agent.name }}
                  </div>
                  <div class="text-xs text-n-slate-10 truncate">
                    {{ agent.email }}
                  </div>
                </div>
              </div>
            </td>
            <td class="py-2 px-2 text-center">
              <span
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                :class="getStatusColor(agent.availability_status)"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-white/80" />
                <span class="text-white">{{
                  getStatusLabel(agent.availability_status)
                }}</span>
              </span>
            </td>
            <td class="py-2 px-2 text-right font-medium text-n-slate-12">
              {{ agent.conversations_count || 0 }}
            </td>
            <td class="py-2 px-2 text-right font-medium text-n-teal-11">
              {{ agent.resolved_count || 0 }}
            </td>
            <td class="py-2 px-2 text-right">
              <span
                :class="
                  agent.resolution_rate >= 70
                    ? 'text-n-teal-11'
                    : agent.resolution_rate >= 50
                      ? 'text-n-amber-11'
                      : 'text-n-ruby-11'
                "
              >
                {{ formatRate(agent.resolution_rate) }}
              </span>
            </td>
            <td class="py-2 px-2 text-right">
              <span
                :class="
                  agent.avg_first_response_minutes <= 5
                    ? 'text-n-teal-11'
                    : agent.avg_first_response_minutes <= 15
                      ? 'text-n-blue-11'
                      : 'text-n-amber-11'
                "
              >
                {{ formatTime(agent.avg_first_response_minutes) }}
              </span>
            </td>
            <td class="py-2 px-2 text-right">
              <span
                :class="
                  agent.csat_score >= 80
                    ? 'text-n-teal-11'
                    : agent.csat_score >= 60
                      ? 'text-n-amber-11'
                      : 'text-n-ruby-11'
                "
              >
                {{ formatRate(agent.csat_score) }}
              </span>
            </td>
            <td class="py-2 px-2 text-right">
              <span
                :class="
                  agent.sla_compliance >= 90
                    ? 'text-n-teal-11'
                    : agent.sla_compliance >= 70
                      ? 'text-n-amber-11'
                      : 'text-n-ruby-11'
                "
              >
                {{ formatRate(agent.sla_compliance) }}
              </span>
            </td>
            <td class="py-2 px-2 text-right text-n-slate-11">
              {{ agent.transfers_out || 0 }}
            </td>
            <td class="py-2 px-2 text-right text-n-slate-11">
              {{ agent.messages_count || 0 }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
